import React, { useCallback, useRef, useState, useEffect } from "react";
import { StyleSheet, TextInput, View, Animated, Platform } from "react-native";
import _ from "lodash";
import { Dropdown } from "react-native-element-dropdown";

import { Button as _Button } from "src/common/assets/mixins";
import variables from "src/common/assets/variables";
import Button from "./button";
import { InputProps } from "src/common/types/input";
import Text from "./text";
import {
  SCREEN_HEIGHT,
  SCREEN_WIDTH,
  TABLET_PC,
} from "../libs/utils/constants";
import { MaterialIcons } from "@expo/vector-icons";
import ControlledTooltip from "./controlledTooltip";
import { MaterialCommunityIcons } from "@expo/vector-icons";

const inputHeight = 50;

const Input = React.forwardRef<TextInput, InputProps>(
  (
    {
      containerStyle,
      disabled = false,
      error,
      value,
      rightView,
      animated = true,
      solid = false,
      dropdownValues = [],
      tooltip,
      showPass,
      ...props
    }: InputProps,
    ref
  ) => {
    const [focused, setFocused] = useState(!_.isEmpty(value));
    const [openTooltip, setOpenTooltip] = useState(false);

    const translateY = useRef(
      new Animated.Value(!_.isEmpty(value) ? -(inputHeight + 2) / 2 : 0)
    ).current;
    const placeholderFontSize = useRef(
      new Animated.Value(!_.isEmpty(value) ? 12 : 16)
    ).current;

    const onFocused = useCallback(() => {
      setFocused(true);
      animateUp();
    }, []);

    const onBlurred = useCallback(() => {
      if (_.isEmpty(value)) {
        animateDown();
      }
      setFocused(false);
    }, [value]);

    const animateUp = () => {
      Animated.timing(translateY, {
        toValue: -(inputHeight + 2) / 2,
        duration: 100,
        useNativeDriver: true,
      }).start();
      Animated.timing(placeholderFontSize, {
        toValue: 12,
        duration: 100,
        useNativeDriver: false,
      }).start();
    };

    const animateDown = () => {
      Animated.timing(translateY, {
        toValue: 0,
        duration: 100,
        useNativeDriver: true,
      }).start();
      Animated.timing(placeholderFontSize, {
        toValue: 16,
        duration: 100,
        useNativeDriver: false,
      }).start();
    };

    useEffect(() => {
      if (_.isEmpty(value) && !focused) {
        onBlurred();
      } else {
        onFocused();
      }
    }, [value]);

    const showError = !_.isEmpty(value) && error && !focused;

    const getInputField = () => {
      if (!dropdownValues.length) {
        return (
          <View style={{ flex: 1, minHeight: 50 }}>
            <TextInput
              {...props}
              onChangeText={(val) => {
                if (
                  val &&
                  (props.keyboardType === "phone-pad" ||
                    props.keyboardType === "number-pad" ||
                    props.keyboardType === "decimal-pad" ||
                    props.keyboardType === "numeric") &&
                  !Number(val)
                ) {
                  return;
                }
                props.onChangeText(val);
              }}
              value={value || ""}
              placeholder={animated ? null : props.placeholder}
              style={[
                styles.TextInput,
                disabled && styles.disabledText,
                solid && { fontSize: 14 },
                {
                  pointerEvents: disabled ? "none" : "auto",
                },
              ]}
              ref={ref}
              readOnly={disabled}
              returnKeyType="done"
              onFocus={onFocused}
              onBlur={onBlurred}
            />
          </View>
        );
      }
    };

    const getDropDown = () => {
      if (dropdownValues.length) {
        const sH = SCREEN_HEIGHT;
        const sW = SCREEN_WIDTH;
        return (
          <View
            style={[
              styles.TextInput,
              { justifyContent: "center", paddingLeft: 0 },
            ]}
          >
            <Dropdown
              dropdownPosition={TABLET_PC ? "bottom" : "top"}
              placeholderStyle={{ paddingLeft: 20 }}
              containerStyle={{
                marginTop: 15,
                marginBottom: TABLET_PC ? undefined : 20,
                backgroundColor: "#F2F1EB",
                borderRadius: 20,
                overflow: "hidden",
              }}
              selectedTextStyle={{ paddingLeft: 20 }}
              activeColor="#F2F1EB"
              data={dropdownValues}
              labelField="label"
              valueField="value"
              value={value}
              renderRightIcon={() => null}
              showsVerticalScrollIndicator={false}
              onChange={(item) => {
                props.onChangeText(item.value);
              }}
            />
            <View style={[styles.absRightView, { pointerEvents: "none" }]}>
              {rightView}
            </View>
          </View>
        );
      }
      return null;
    };

    return (
      <>
        <View style={containerStyle}>
          <View
            style={[
              styles.container,
              solid && { backgroundColor: "#F2F1EB", borderColor: "#F2F1EB" },
              showError && styles.errorContainer,
            ]}
          >
            {getInputField()}
            {!tooltip && props.togglePass && (
              <View>
                <Button
                  solid={false}
                  style={styles.viewPass}
                  onPress={() => props.togglePass(props.secureTextEntry)}
                >
                  <MaterialCommunityIcons
                    name={`eye${showPass ? "-off" : ""}`}
                    size={24}
                    color={variables.$icon}
                  />
                </Button>
              </View>
            )}
            {!props.togglePass && tooltip && (
              <ControlledTooltip
                width={250}
                height={80}
                open={openTooltip}
                setOpenTooltip={setOpenTooltip}
                popover={<Text>{tooltip}</Text>}
                content={() => (
                  <Button
                    solid={false}
                    style={styles.viewPass}
                    onPress={() => setOpenTooltip(true)}
                  >
                    <MaterialIcons
                      name="info-outline"
                      size={24}
                      color={variables.$icon}
                    />
                  </Button>
                )}
              />
            )}
            {props.placeholder && animated && (
              <Animated.View
                style={[
                  styles.placeholderContainer,
                  focused && styles.focused,
                  { transform: [{ translateY }] },
                ]}
              >
                <Animated.Text
                  style={[
                    styles.placeholder,
                    focused && styles.focusedPlaceholder,
                    showError && styles.errorText,
                    { fontSize: placeholderFontSize },
                  ]}
                >
                  {props.placeholder}
                </Animated.Text>
              </Animated.View>
            )}
            {/* {rightView} */}
            {dropdownValues.length === 0 && rightView}
            {getDropDown()}
          </View>
          {showError && <Text style={styles.error}>{error}</Text>}
        </View>
      </>
    );
  }
);

const styles = StyleSheet.create({
  TextInput: {
    height: inputHeight,
    padding: 15,
    paddingLeft: 20,
    paddingRight: 20,
    fontFamily: "Montserrat",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 0,
    flex: 1,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        outlineStyle: "none",
      },
    }),
  },
  absRightView: {
    position: "absolute",
    right: 0,
  },
  errorText: {
    color: variables.$danger,
  },
  errorContainer: {
    borderColor: variables.$danger,
  },
  error: {
    color: variables.$danger,
    fontSize: 12,
    marginLeft: 4,
    marginTop: 4,
  },
  placeholder: {
    color: variables.$textLight,
    fontFamily: "Montserrat",
    fontWeight: "400",
    lineHeight: 20,
    letterSpacing: 0,
  },
  placeholderContainer: {
    position: "absolute",
    zIndex: -1,
    left: 15,
    justifyContent: "center",
    backgroundColor: "#fff",
    paddingHorizontal: 5,
    pointerEvents: "none",
  },
  focused: {
    backgroundColor: "#fff",
    left: 15,
  },
  focusedPlaceholder: {
    fontSize: 12,
    lineHeight: 15,
  },
  disabledText: {
    color: variables.$disabledText,
  },
  container: {
    borderRadius: 15,
    borderWidth: 1,
    borderColor: "#B2BEC3",
    flexDirection: "row",
    alignItems: "center",
  },
  viewPass: {
    height: 50,
    width: 50,
    minWidth: undefined,
  },
});

export default Input;
